// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LazyImageStyle } from "@conklin/MobiusOverrides/LazyImage";
import { StyledIcon } from "@conklin/MobiusOverrides/Link/Link";
import { DEFAULT_MENU_WIDTH } from "@conklin/MobiusOverrides/Menu/Menu";
import { mainNavigationStyles } from "@conklin/Overrides/Widgets/Header/MainNavigation";
import { constants } from "@conklin/XDL/constants";
import IconArrowRight from "@conklin/XDL/Icons/IconArrowRight";
import IconHeaderCaretSmallDown from "@conklin/XDL/Icons/IconHeaderCaretSmallDown";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, createLinkIconStyles } from "@conklin/XDL/Utilities";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

mainNavigationStyles.container = {
    css: css`
        display: flex;
        column-gap: 64px;
    `,
};

mainNavigationStyles.menuItem = {
    css: css`
        display: flex;
        align-items: center;

        ${TypographyStyle} {
            display: flex;
            align-items: center;
            font-size: 18px;
            line-height: 22px;
            font-weight: ${fontConfig.weight.black};
            text-transform: capitalize;
        }

        &&& {
            ${StyledIcon} {
                color: ${color("common.ui4")};
            }
            &:focus {
                outline: none;
            }

            &:hover {
                background-color ${TypographyStyle} {
                    color: ${color("common.ui10")};
                }

                ${StyledIcon} {
                    color: ${color("common.ui10")};
                    transform: rotate(180deg);
                }
            }
        }
    `,
};

mainNavigationStyles.menuItemTypography = {
    color: "text.main",
};

mainNavigationStyles.itemWrapper = {
    css: css`
        position: relative;
        overflow: visible;
        display: flex;
        &:hover span + div {
            display: block;
        }
    `,
};

mainNavigationStyles.menuItemIcon = {
    src: IconHeaderCaretSmallDown,
    color: "secondary",
    size: 11,
    css: css`
        margin: 0;
        position: relative;
        left: 8px;
        top: 2px;
        &,
        & svg {
            height: auto;
        }
    `,
};

mainNavigationStyles.cascadingMenu = {
    cssOverrides: {
        contentWrapper: {
            css: css`
                background-color: ${color("common.ui1")};
                position: absolute;
                left: 100%;
                top: 0;
                padding: 12px;
                height: 100%;
                max-height: calc(calc(100vh - ${constants.header.size.height}px) * 0.8);
                width: ${DEFAULT_MENU_WIDTH}px;
                display: flex;
                flex-direction: column;
                border: 1px solid ${color("common.ui6")};
                border-top: none;
                ${LazyImageStyle} {
                    justify-content: start;
                }
            `,
        },
        contentTitle: {
            variant: "h5",
            css: css`
                margin-bottom: 12px;
            `,
        },
        contentText: {
            variant: "h6",
            weight: fontConfig.weight.regular,
            css: css`
                line-height: 22px;
                margin-bottom: 16px;
            `,
        },
        contentLink: {
            ...createLinkIconStyles(IconArrowRight, "small", "right", false, {
                css: css`
                    margin-bottom: 12px;
                    justify-content: flex-end;
                    width: fit-content;
                `,
            }),
        },
        contentImage: {
            width: "100%",
            mergeCss: true,
            css: css`
                max-height: 400px;
                object-fit: contain;
            `,
        },
        brandLinkWrapper: {
            css: css`
                padding: 6px 12px 4px;
            `,
        },
        brandLink: {
            css: css`
                padding-bottom: 12px;
                border-bottom: 1px solid ${color("common.border")};
                width: 100%;
            `,
            typographyProps: {
                lineHeight: "17px",
                weight: fontConfig.weight.regular,
                color: "text.main",
                css: css`
                    &&& {
                        color: ${color("text.main")};
                    }
                `,
            },
        },
    },
};

mainNavigationStyles.mobileMenuWrapper = {
    css: css`
        height: 16px;
    `,
};
